/** A YouTube video id: eleven URL-safe base64 characters. */
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/u;

/** Hosts that serve YouTube videos, with and without the www and mobile prefixes. */
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

/** Short-link hosts, whose first path segment is the id. */
const SHORT_HOSTS = new Set(["youtu.be", "www.youtu.be"]);

/** Path prefixes whose next segment is the video id: /embed/ID, /shorts/ID, /live/ID, /v/ID. */
const ID_PATH_PREFIXES = new Set(["embed", "shorts", "live", "v"]);

/**
 * Parse what a pro pastes: a full URL, or one without its scheme.
 * @param {string} input The text as typed.
 * @returns {URL | null} The URL, or null when it is not one.
 */
const parseUrl = (input: string): URL | null => {
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//iu.test(input) ? input : `https://${input}`;
  try {
    return new URL(withScheme);
  } catch {
    return null;
  }
};

/**
 * The id a youtube.com path or query names.
 * @param {URL} url A URL on a YouTube host.
 * @param {string[]} segments Its path, split on slashes.
 * @returns {string | null} The candidate id, unchecked.
 */
const candidateFromYoutubePath = (url: URL, segments: string[]): string | null => {
  if (segments[0] === "watch") {
    return url.searchParams.get("v");
  }
  if (segments.length >= 2 && ID_PATH_PREFIXES.has(segments[0])) {
    return segments[1];
  }
  return null;
};

/**
 * The id from a URL's path or query, by host.
 * @param {URL} url A parsed URL.
 * @returns {string | null} The candidate id, unchecked, or null when the URL holds none.
 */
const candidateFromUrl = (url: URL): string | null => {
  const host = url.hostname.toLowerCase();
  const segments = url.pathname.split("/").filter((segment) => segment !== "");
  if (SHORT_HOSTS.has(host)) {
    return segments[0] ?? null;
  }
  if (YOUTUBE_HOSTS.has(host)) {
    return candidateFromYoutubePath(url, segments);
  }
  return null;
};

/**
 * The id a link holds, when it is a valid one.
 * @param {string} link A trimmed, non-empty link.
 * @returns {string | null} The id, or null.
 */
const idFromLink = (link: string): string | null => {
  const url = parseUrl(link);
  const candidate = url === null ? null : candidateFromUrl(url);
  return candidate !== null && VIDEO_ID.test(candidate) ? candidate : null;
};

/**
 * The video id from any common form of YouTube link, or a bare id.
 * @param {string | null | undefined} input What the pro pasted.
 * @returns {string | null} The eleven-character id, or null when it is not a YouTube video.
 */
const youtubeVideoId = (input: string | null | undefined): string | null => {
  const trimmed = (input ?? "").trim();
  if (trimmed === "") {
    return null;
  }
  if (VIDEO_ID.test(trimmed)) {
    return trimmed;
  }
  return idFromLink(trimmed);
};

/**
 * The privacy-enhanced embed address for a video: no cookies until the visitor presses play.
 * @param {string} videoId An id from youtubeVideoId.
 * @returns {string} The iframe src.
 */
const youtubeEmbedUrl = (videoId: string): string => `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;

export { youtubeEmbedUrl, youtubeVideoId };
